import React, { useRef, useEffect } from 'react';
import { AlertCircle, Check, Scissors, FileText, Tag } from 'lucide-react';
import { TimelineEvent } from './history-timeline';
import { format, parseISO } from 'date-fns';

interface HorizontalTimelineProps {
  events: TimelineEvent[];
  onEventClick: (event: TimelineEvent) => void;
  className?: string;
}

/**
 * A horizontal, scrollable timeline component for medical history
 * Shows events as pill-shaped cards organized chronologically
 */
const HorizontalTimeline: React.FC<HorizontalTimelineProps> = ({
  events,
  onEventClick,
  className = '',
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Sort events by date (most recent first)
  const sortedEvents = [...events].sort((a, b) => {
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });
  
  // Group events by year
  const eventsByYear: Record<number, TimelineEvent[]> = {};
  sortedEvents.forEach(event => {
    const date = new Date(event.date);
    const year = date.getFullYear();
    if (!eventsByYear[year]) {
      eventsByYear[year] = [];
    }
    eventsByYear[year].push(event);
  });
  
  // Get category icon based on event category
  const getCategoryIcon = (category?: string) => {
    switch (category?.toLowerCase()) {
      case 'condition':
      case 'diagnosis':
        return <AlertCircle className="w-4 h-4 mr-1.5" />;
      case 'surgery':
      case 'procedure':
        return <Scissors className="w-4 h-4 mr-1.5" />;
      case 'document':
      case 'report':
        return <FileText className="w-4 h-4 mr-1.5" />;
      case 'resolution':
      case 'cured':
        return <Check className="w-4 h-4 mr-1.5" />;
      default:
        return <Tag className="w-4 h-4 mr-1.5" />;
    }
  };
  
  // Get color based on severity
  const getSeverityColor = (severity?: 'low' | 'medium' | 'high') => {
    switch (severity) {
      case 'high':
        return 'border-[var(--status-error)] bg-[var(--status-error-subtle)]';
      case 'medium':
        return 'border-[var(--status-warning)] bg-[var(--status-warning-subtle)]';
      case 'low':
        return 'border-[var(--status-success)] bg-[var(--status-success-subtle)]';
      default:
        return 'border-[var(--brand-primary)] bg-[var(--background-subtle)]';
    }
  };
  
  // Format date to display
  const formatDate = (dateString: string) => {
    try {
      const date = parseISO(dateString);
      return format(date, 'MMM d, yyyy');
    } catch (error) {
      console.error("Date parsing error:", error);
      return dateString;
    }
  };
  
  // Scroll left
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };
  
  // Scroll right
  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };
  
  // Enable drag-to-scroll
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    
    let isDown = false;
    let startX: number;
    let scrollLeft: number;
    
    const handleMouseDown = (e: MouseEvent) => {
      isDown = true;
      scrollContainer.classList.add('cursor-grabbing');
      startX = e.pageX - scrollContainer.offsetLeft;
      scrollLeft = scrollContainer.scrollLeft;
    };
    
    const handleMouseLeave = () => {
      isDown = false;
      scrollContainer.classList.remove('cursor-grabbing');
    };
    
    const handleMouseUp = () => {
      isDown = false;
      scrollContainer.classList.remove('cursor-grabbing');
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - scrollContainer.offsetLeft;
      const walk = (x - startX) * 2; // Adjust scrolling speed
      scrollContainer.scrollLeft = scrollLeft - walk;
    };
    
    scrollContainer.addEventListener('mousedown', handleMouseDown);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);
    scrollContainer.addEventListener('mouseup', handleMouseUp);
    scrollContainer.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      scrollContainer.removeEventListener('mousedown', handleMouseDown);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
      scrollContainer.removeEventListener('mouseup', handleMouseUp);
      scrollContainer.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  if (events.length === 0) {
    return (
      <div className={`text-center p-6 text-[var(--text-secondary)] ${className}`}>
        <p>No medical history records available.</p>
      </div>
    );
  }
  
  return (
    <div className={`relative ${className}`}>
      {/* Scroll buttons */}
      <button
        onClick={scrollLeft}
        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800 rounded-full shadow-md p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label="Scroll left"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
      </button>
      
      <button
        onClick={scrollRight}
        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white dark:bg-gray-800 rounded-full shadow-md p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
        aria-label="Scroll right"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </button>
      
      {/* Timeline container */}
      <div 
        ref={scrollContainerRef}
        className="overflow-x-auto py-8 cursor-grab no-scrollbar"
        aria-label="Medical History Timeline"
      >
        <div className="flex min-w-max">
          {Object.entries(eventsByYear)
            .sort(([yearA], [yearB]) => Number(yearB) - Number(yearA))
            .map(([year, yearEvents]) => (
              <div key={year} className="mx-4 first:ml-12 last:mr-12">
                <div className="text-center mb-6">
                  <div className="inline-block px-3 py-1 bg-[var(--brand-primary)] text-white rounded-full font-medium">
                    {year}
                  </div>
                </div>
                
                <div className="relative flex flex-col space-y-3 border-l-2 border-[var(--border-subtle)] pl-6">
                  {yearEvents.map((event) => (
                    <div key={event.id} className="relative">
                      {/* Timeline dot */}
                      <div className="absolute left-[-15px] top-5 w-4 h-4 rounded-full border-2 border-[var(--brand-primary)] bg-white dark:bg-gray-800"></div>
                      
                      {/* Event pill */}
                      <div 
                        className={`
                          px-4 py-3 rounded-full border-2 min-w-[200px] max-w-md
                          cursor-pointer transform transition-transform hover:scale-105
                          ${getSeverityColor(event.severity)}
                        `}
                        onClick={() => onEventClick(event)}
                      >
                        <div className="flex items-center">
                          {getCategoryIcon(event.category)}
                          <span className="font-medium text-sm truncate">{event.title}</span>
                        </div>
                        <div className="text-xs text-[var(--text-secondary)] mt-1">{formatDate(event.date)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
      
      {/* Instructions */}
      <div className="text-center text-xs text-[var(--text-secondary)] mt-1">
        Drag to scroll or use the arrow buttons • Click an event for details
      </div>
    </div>
  );
};

export default HorizontalTimeline; 